// src/app/components/invitations/services/invitation-mock.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  Invitation, InvitationDraft, InvitationQuery, InvitationStatus,
  Organizer, Participant, RosterRef, ScheduleStep,
} from '../models/invitation.model';
import { INVITATIONS_SEED } from '../data/invitations.seed';
import { OrgMockService } from '../../../service/org-mock.service';

// v7: 'draft' removed (v6: session lifted out of invitation; v5: cross-org eligible pools for Reports; v4: invitation.session; v3: 'scheduled' removed; v2: per-participant `unlocked`)
const STORAGE_KEY = 'fs_mock_invitations_v7';

/** The signed-in user, mocked for the sample-data branch. Seeds the default wizard organizer. */
const CURRENT_USER = { name: 'Test Owner', email: 'testowner@foursightonline.com' };

@Injectable({ providedIn: 'root' })
export class InvitationMockService {
  private subject = new BehaviorSubject<Invitation[]>([]);
  private idCounter = 1;

  constructor(private orgs: OrgMockService) {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list = saved ? JSON.parse(saved) : clone(INVITATIONS_SEED);
    this.subject.next(list);
    this.idCounter = this.nextCounter(list);
  }

  /** Restore the seed (demos / tests). */
  reset(): void {
    this.orgs.reset();
    const seed = clone(INVITATIONS_SEED);
    this.commit(seed);
    this.idCounter = this.nextCounter(seed);
  }

  list(q: InvitationQuery): Observable<{ rows: Invitation[]; count: number }> {
    let rows = this.subject.value.filter(i => !!q.archived === !!i.archived);
    if (q.status && q.status !== 'all') rows = rows.filter(i => i.status === q.status);
    if (q.search) {
      const s = q.search.toLowerCase();
      rows = rows.filter(i =>
        i.name.toLowerCase().includes(s) ||
        i.rosterName.toLowerCase().includes(s) ||
        i.organizers.some(o => o.email.toLowerCase().includes(s) || o.name.toLowerCase().includes(s)));
    }
    if (q.sort) {
      const { prop, dir } = q.sort;
      // Presentation (default) sorts "present to future": upcoming dates ascending (today
      // first), then already-past presentations (most recent past first). Open invitations sort
      // by date — status is not a factor between them — but CLOSED invitations sink
      // to the bottom, kept out of the active ordering (archived are already filtered out).
      // Status only groups the list (open → closed) when explicitly sorted on.
      const statusRank: Record<string, string> = { open: '1', closed: '2' };
      const today = new Date().toISOString().slice(0, 10);
      const presentToFuture = (i: Invitation): string => {
        const d = (i.schedule.find(s => s.name === 'presentation')?.date ?? '').slice(0, 10);
        // Upcoming ascending (0 + date), then past descending (1 + inverted date digits).
        return d >= today ? '0' + d : '1' + d.replace(/\d/g, c => `${9 - +c}`);
      };
      // Closed invitations are demoted below every non-closed one, then ordered by date.
      const presentationKey = (i: Invitation): string => (i.status === 'closed' ? '1' : '0') + presentToFuture(i);
      const valueOf = (i: Invitation): string =>
        prop === 'presentation' ? presentationKey(i)
        : prop === 'deadline' ? (i.schedule.find(s => s.name === 'deadline')?.date ?? '')
        : prop === 'status' ? statusRank[i.status] ?? '9'
        : `${(i as any)[prop]}`;
      rows = [...rows].sort((a, b) => {
        const av = valueOf(a), bv = valueOf(b);
        const cmp = dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return (cmp === 0 && prop === 'status') ? presentToFuture(a).localeCompare(presentToFuture(b)) : cmp;
      });
    }
    const count = rows.length;
    if (q.pageSize != null) {
      const page = q.page ?? 0;
      rows = rows.slice(page * q.pageSize, page * q.pageSize + q.pageSize);
    }
    return of({ rows, count });
  }

  getById(id: string): Observable<Invitation | undefined> {
    return of(this.subject.value.find(i => i.id === id));
  }

  /** The wizard only creates active invitations — there is no draft state. */
  create(payload: InvitationDraft): Observable<Invitation> {
    const roster = this.orgs.getById(payload.rosterId);
    const now = '1970-01-01T00:00:00Z'; // deterministic; replaced by API later
    const inv: Invitation = {
      id: this.newId('inv'),
      name: payload.name,
      rosterId: payload.rosterId,
      rosterName: roster?.name ?? '',
      language: payload.language,
      status: 'open',
      estimatedParticipants: payload.estimatedParticipants,
      whoInvites: payload.whoInvites,
      whoPays: payload.whoPays,
      howResults: payload.howResults,
      schedule: payload.schedule,
      organizers: payload.organizers.map(o => ({ ...o, id: o.id || this.newId('o') })),
      participants: [],
      email: payload.email,
      messages: payload.messages,
      scoringKeys: {
        available: roster?.availableKeys ?? 0,
        needed: Math.max(0, payload.estimatedParticipants - (roster?.availableKeys ?? 0)),
      },
      followupEmailsEnabled: payload.followupEmailsEnabled,
      archived: false,
      createdAt: now, updatedAt: now,
    };
    this.commit([...this.subject.value, inv]);
    return of(inv);
  }

  update(id: string, patch: Partial<Invitation>): Observable<Invitation> {
    return this.mutate(id, inv => Object.assign(inv, patch));
  }

  updateStatus(id: string, status: InvitationStatus): Observable<Invitation> {
    return this.mutate(id, inv => { inv.status = status; });
  }

  archive(id: string, archived: boolean): Observable<Invitation> {
    return this.mutate(id, inv => { inv.archived = archived; });
  }

  /** Permanently delete an invitation (listing only allows this while it has no participants). */
  remove(id: string): Observable<void> {
    this.commit(this.subject.value.filter(i => i.id !== id));
    return of(void 0);
  }

  addOrganizer(id: string, org: Omit<Organizer, 'id'>): Observable<Invitation> {
    return this.mutate(id, inv => { inv.organizers = [...inv.organizers, { ...org, id: this.newId('o') }]; });
  }

  updateOrganizer(id: string, org: Organizer): Observable<Invitation> {
    return this.mutate(id, inv => { inv.organizers = inv.organizers.map(o => o.id === org.id ? org : o); });
  }

  removeOrganizer(id: string, organizerId: string): Observable<Invitation> {
    return this.mutate(id, inv => { inv.organizers = inv.organizers.filter(o => o.id !== organizerId); });
  }

  updateSchedule(id: string, steps: ScheduleStep[]): Observable<Invitation> {
    return this.mutate(id, inv => { inv.schedule = steps; });
  }

  updateParticipant(id: string, participant: Participant): Observable<Invitation> {
    return this.mutate(id, inv => { inv.participants = inv.participants.map(p => p.id === participant.id ? participant : p); });
  }

  removeParticipant(id: string, participantId: string): Observable<Invitation> {
    return this.mutate(id, inv => {
      inv.participants = inv.participants.filter(p => p.id !== participantId);
      recomputeNeeded(inv);
    });
  }

  addParticipants(id: string, people: Pick<Participant, 'firstName' | 'lastName' | 'email'>[]): Observable<Invitation> {
    return this.mutate(id, inv => {
      const added: Participant[] = people.map(p => ({ ...p, id: this.newId('p'), state: 'not_started' }));
      inv.participants = [...inv.participants, ...added];
    });
  }

  /** Transfer assessment scoring keys from a source roster to this invitation. */
  transferKeys(id: string, fromRosterId: string, requested: number): Observable<Invitation> {
    const want = Math.max(0, requested);
    // adjustKeys clamps at the org's balance and reports what it could actually move.
    const count = this.orgs.getById(fromRosterId) ? -this.orgs.adjustKeys(fromRosterId, -want) : want;
    return this.mutate(id, inv => {
      inv.scoringKeys.available += count;
      recomputeNeeded(inv);
    });
  }

  /**
   * Spend scoring keys to unlock specific scored-but-locked profiles (1 key each).
   * Only ids that are currently scored & locked are charged. Throws if the available
   * balance can't cover the cost — the caller gates this and should never trigger it.
   */
  unlockParticipants(id: string, participantIds: string[]): Observable<Invitation> {
    return this.mutate(id, inv => {
      const ids = new Set(participantIds);
      const targets = inv.participants.filter(p => ids.has(p.id) && p.state === 'scored' && !p.unlocked);
      const cost = targets.length;
      if (cost === 0) { return; }
      if (inv.scoringKeys.available < cost) { throw new Error('Not enough scoring keys'); }
      targets.forEach(p => { p.unlocked = true; });
      inv.scoringKeys.available -= cost;
      recomputeNeeded(inv);
    });
  }

  getRosters(): Observable<RosterRef[]> {
    return this.orgs.list();
  }

  /** Create a new organization inline (e.g. from the invitation wizard). New orgs start
   * with no scoring keys — the wizard's shortfall notice guides the user from there. */
  addRoster(name: string, availableKeys = 0): Observable<RosterRef> {
    return this.orgs.add(name.trim(), availableKeys);
  }

  /** The session rule: scored AND unlocked. Anything else is excluded entirely. */
  eligibleParticipants(inv: Invitation): Participant[] {
    return inv.participants.filter(p => p.state === 'scored' && !!p.unlocked);
  }

  /**
   * Resolve participant ids to Participant objects across ALL invitations, preserving the given
   * order and dropping ids that resolve to nothing. Sessions reference participants that span
   * invitations, so the board resolves group members through here rather than off one invitation.
   * Participant ids are globally unique (one counter, /^(?:inv|o|p)-\d+$/), so a hit is unambiguous.
   */
  participantsByIds(ids: string[]): Participant[] {
    const index = new Map<string, Participant>();
    for (const inv of this.subject.value) {
      for (const p of inv.participants) { index.set(p.id, p); }
    }
    return ids.map(id => index.get(id)).filter((p): p is Participant => !!p);
  }

  /** The signed-in user (mocked) — used to pre-seed the default organizer in the create wizard. */
  currentUser(): { name: string; email: string } {
    return { ...CURRENT_USER };
  }

  // --- internals ---
  private mutate(id: string, fn: (inv: Invitation) => void): Observable<Invitation> {
    const list = clone(this.subject.value);
    const inv = list.find(i => i.id === id);
    if (!inv) throw new Error(`Invitation ${id} not found`);
    fn(inv);
    this.commit(list);
    return of(inv);
  }

  private commit(list: Invitation[]): void {
    this.subject.next(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  private newId(prefix: string): string {
    return `${prefix}-${this.idCounter++}`;
  }

  /** The id the next create() will assign — lets the wizard preview the real assessment link. */
  peekNextInvitationId(): string {
    return `inv-${this.idCounter}`;
  }

  /** Derive the next id counter from persisted data so generated ids never collide after reload. */
  private nextCounter(list: Invitation[]): number {
    let max = 0;
    const scan = (id: string) => {
      const m = /^(?:inv|o|p)-(\d+)$/.exec(id);
      if (m) { max = Math.max(max, +m[1]); }
    };
    list.forEach(inv => {
      scan(inv.id);
      inv.organizers.forEach(o => scan(o.id));
      inv.participants.forEach(p => scan(p.id));
    });
    return max + 1;
  }

  /** Stream for components that want live updates. */
  changes(): Observable<Invitation[]> {
    return this.subject.asObservable();
  }
}

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }

/** Keys still needed to unlock everyone who's completed = locked scored profiles minus the balance. */
function recomputeNeeded(inv: Invitation): void {
  const locked = inv.participants.filter(p => p.state === 'scored' && !p.unlocked).length;
  inv.scoringKeys.needed = Math.max(0, locked - inv.scoringKeys.available);
}
